/**
 * Shared mapper for transforming a LocateOrder into a FlattenedLocateResultRow.
 *
 * Used by:
 * - LocatesWidgetUpdateProvider (socket updates)
 * - LocatesV2WidgetModelImpl (API create-order responses)
 * - LocatesWorkerImpl (worker storage)
 *
 * Also exports extractAutoAcceptFields for use by other model code that shares
 * the auto-accept extraction logic.
 */
import { Instant } from '@js-joda/core';
import type { AutoAcceptTrigger, LocateOrder } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { FlattenedLocateResultRow } from '@thinkalpha/ui-types/types/labModern';

/**
 * Extract all auto-accept fields from an autoAccept config.
 *
 * Always returns autoAcceptEnabled. When enabled with triggers:
 * - LocateRateTrigger: autoAcceptPrice = maxRate, autoAcceptType = 'price'
 * - SecurityPriceTrigger: autoAcceptPrice = maxRatePercent, autoAcceptType = 'percent'
 *
 * Falls back to the legacy price field (default 0) when no triggers are present.
 */
export function extractAutoAcceptFields(
    autoAccept: { enabled: boolean; price?: number; triggers?: AutoAcceptTrigger[] } | undefined,
): Pick<FlattenedLocateResultRow, 'autoAcceptEnabled' | 'autoAcceptPrice' | 'autoAcceptType'> {
    if (!autoAccept?.enabled) {
        return { autoAcceptEnabled: autoAccept?.enabled ?? false };
    }

    const trigger = autoAccept.triggers?.[0];

    // No triggers — fall back to legacy price field
    if (!trigger) {
        return {
            autoAcceptEnabled: true,
            autoAcceptPrice: autoAccept.price ?? 0,
            autoAcceptType: 'price',
        };
    }

    if (trigger.type === 'locate-rate') {
        return {
            autoAcceptEnabled: true,
            autoAcceptPrice: trigger.maxRate,
            autoAcceptType: 'price',
        };
    }

    if (trigger.type === 'security-price') {
        return {
            autoAcceptEnabled: true,
            autoAcceptPrice: trigger.maxRatePercent,
            autoAcceptType: 'percent',
        };
    }

    // Unknown trigger type — enabled but no price/type info
    return { autoAcceptEnabled: true };
}

// -- Auto-accept display formatters --

const autoAcceptPriceFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
});

const autoAcceptPercentFormatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
});

/**
 * Format an auto-accept price value as a dollar amount, e.g. `$0.0200`.
 */
export function formatAutoAcceptPrice(price: number): string {
    return `$${autoAcceptPriceFormatter.format(price)}`;
}

/**
 * Format an auto-accept percent value, e.g. `0.01` → `1.00%`.
 */
export function formatAutoAcceptPercent(value: number): string {
    return autoAcceptPercentFormatter.format(value);
}

/**
 * Format an auto-accept value using the appropriate formatter based on type.
 * Returns an empty string when no price is available.
 */
export function formatAutoAcceptValue(
    autoAcceptPrice: number | undefined,
    autoAcceptType: FlattenedLocateResultRow['autoAcceptType'],
): string {
    if (autoAcceptPrice === undefined) {
        return '';
    }

    if (autoAcceptType === 'percent') {
        return formatAutoAcceptPercent(autoAcceptPrice);
    }

    return formatAutoAcceptPrice(autoAcceptPrice);
}

/**
 * Transform a LocateOrder into a FlattenedLocateResultRow.
 *
 * @param order - The LocateOrder from the socket or API.
 * @param idSuffix - A suffix appended to the order ID to create a unique row ID
 *                   (e.g. 'update', 'api', 'worker') to distinguish the source.
 */
export function mapLocateOrderToRow(order: LocateOrder, idSuffix: string): FlattenedLocateResultRow {
    return {
        type: 'data',
        id: `${order.id}-${idSuffix}`,
        orderId: order.id,
        resultId: order.id,
        stableOrderId: order.id,
        symbol: order.symbol,
        accountId: order.accountId ?? '',
        createdAt: typeof order.createdTime === 'string' ? Instant.parse(order.createdTime) : order.createdTime,
        createdBy: order.userId ?? '',
        userId: order.userId,
        marketParticipantId: '',
        orderQuantity: order.quantity,
        borrowStatus: order.borrowStatus ?? '',
        routeName: order.requestedProviderId ?? null,
        offeredQuantity: order.quantity,
        price: order.price ?? null,
        billingPrice: null,
        providerId: order.resolvedProviderId ?? order.requestedProviderId ?? '',
        resultState: order.orderStatus,
        acceptedAt:
            order.orderStatus === 'accepted' && order.lastUpdateTime
                ? typeof order.lastUpdateTime === 'string'
                    ? Instant.parse(order.lastUpdateTime)
                    : order.lastUpdateTime
                : null,
        actionableAt: null,
        rejectedAt:
            order.orderStatus === 'rejected' && order.lastUpdateTime
                ? typeof order.lastUpdateTime === 'string'
                    ? Instant.parse(order.lastUpdateTime)
                    : order.lastUpdateTime
                : null,
        rejectedBy: null,
        rejectReason: order.rejectReason ?? null,
        injectionTargetName: undefined,
        integration: null,
        offerId: order.offerId,
        ...extractAutoAcceptFields(order.autoAccept),
        clientTrackingId: order.optimisticOrderId ?? order.clientTrackingId ?? null,
    };
}
